import React from "react";
import {
  HtmlEditor,
  Image,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
  ToolbarType,
} from "@syncfusion/ej2-react-richtexteditor";
import { MentionComponent } from "@syncfusion/ej2-react-dropdowns";
import {
  MentionableFragment,
  MentionableUserFragment,
} from "__generated__/graphql";
import { ItemTemplate } from "./ItemTemplate";
import { DisplayTemplate } from "./DisplayTemplate";
import { Box } from "@mui/material";
import "./RichTextEditor.css";
import { FieldLabel } from "common/fieldLabel/FieldLabel";
import { useTranslation } from "react-i18next";

const toolbarSettings: ToolbarSettingsModel = {
  items: [
    "Bold",
    "Italic",
    "Underline",
    "StrikeThrough",
    "|",
    "OrderedList",
    "UnorderedList",
    "|",
    "CreateLink",
    "Image",
    "|",
    "FullScreen",
    "|",
    "Undo",
    "Redo",
  ],
  type: ToolbarType.Expand,
};

interface IRichTextEditorProps {
  imageSizeLimitKb?: number;
  value: string;
  setValue?: (value: string) => void;
  placeholder?: string;
  mentionableUsers?: (MentionableUserFragment | MentionableFragment)[];
  isPreviewMode?: boolean;
  label?: string;
  showExternalLabel?: boolean;
}
export const RichTextEditor: React.FC<IRichTextEditorProps> = ({
  imageSizeLimitKb,
  value,
  setValue,
  placeholder,
  mentionableUsers,
  isPreviewMode = false,
  label,
  showExternalLabel,
}) => {
  const { t: translate } = useTranslation("common");
  const uniqId = "commentEditor" + Date.now();
  const actionBegineHandler = (args) => {
    // so type enter after mentioning someone doesn't create a new line
    if (args.requestType === "EnterAction") {
      args.cancel = true;
    }
  };
  const renderRichTextEditor = (
    <Box
      sx={
        isPreviewMode
          ? {
              "& .e-rte-container": {
                border: "none",
              },
              "& .e-richtexteditor .e-rte-content .e-content": {
                minHeight: "auto",
                padding: 0,
              },
            }
          : undefined
      }
    >
      <RichTextEditorComponent
        readonly={isPreviewMode}
        inlineMode={
          isPreviewMode
            ? {
                enable: true,
              }
            : undefined
        }
        id={uniqId}
        autoSaveOnIdle
        value={value}
        placeholder={placeholder}
        change={(args) => {
          setValue?.(args.value);
        }}
        saveInterval={10}
        toolbarSettings={toolbarSettings}
        insertImageSettings={{
          saveFormat: "Base64",
        }}
        imageSelected={(args) => {
          const sizeLimitOrDefault = imageSizeLimitKb || 1024;
          const maxSizeInBytes = sizeLimitOrDefault * 1024;
          if (args.filesData[0].size > maxSizeInBytes) {
            alert(
              translate("rich_text_editor.image_size_limit_exceeded", {
                limit: sizeLimitOrDefault,
              }),
            );
            args.cancel = true;
          }
        }}
        actionBegin={actionBegineHandler}
        htmlAttributes={{
          "aria-label":
            label || translate("rich_text_editor.default_aria_label"),
          "aria-labelledby": uniqId,
        }}
      >
        <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar]} />
      </RichTextEditorComponent>
      {mentionableUsers && (
        <MentionComponent
          target={`#${uniqId}_rte-edit-view`} // matches the id of the RichTextEditorComponent, unique so if there are multiple editors on the page, the mentions don't get mixed up
          suggestionCount={8}
          showMentionChar={false}
          allowSpaces={true}
          dataSource={mentionableUsers as { [key: string]: any }[]}
          popupWidth="250px"
          popupHeight="200px"
          fields={{ text: "employee.email" }}
          itemTemplate={(data) => <ItemTemplate data={data} />}
          displayTemplate={(data) => <DisplayTemplate data={data} />}
        />
      )}
    </Box>
  );
  return showExternalLabel ? (
    <FieldLabel label={label} labelId={uniqId}>
      {renderRichTextEditor}
    </FieldLabel>
  ) : (
    renderRichTextEditor
  );
};
